import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";

type MatrixCell = {
  skillId: number;
  trainedOn: string | null;
  expiresOn: string | null;
  status: "missing" | "current" | "expired";
};

type MatrixPayload = {
  skills: { id: number; name: string }[];
  rows: {
    staffId: number;
    fullName: string;
    active: boolean;
    jobTitle: string | null;
    cells: MatrixCell[];
  }[];
};

function cellText(cell: MatrixCell): string {
  if (cell.status === "missing") return "—";
  if (cell.status === "expired") return "Expired";
  return cell.trainedOn ?? "Current";
}

export function PeopleTrainingMatrixPage() {
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const matrixQuery = useQuery({
    queryKey: ["people", "training-matrix"],
    queryFn: () => apiFetch<MatrixPayload>("/api/people/training-matrix"),
  });

  const payload = matrixQuery.data;
  const visible = useMemo(() => {
    const rows = payload?.rows ?? [];
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (activeOnly && !row.active) return false;
      if (needle === "") return true;
      return `${row.fullName} ${row.jobTitle ?? ""}`.toLowerCase().includes(needle);
    });
  }, [payload, search, activeOnly]);

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Each column is an active skill. A date means the qualification is current. Expired means the expiration date has passed. An em dash means there is no record. Add skills on the Skills page, then record a date on the person.">
        Training
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label htmlFor="people-training-search">Search people</Label>
            <Input
              id="people-training-search"
              type="search"
              className="mt-1"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by name or title..."
            />
          </div>
          <Button type="button" size="sm" variant={activeOnly ? "primary" : "secondary"} onClick={() => setActiveOnly((current) => !current)}>
            {activeOnly ? "Active only" : "Including inactive"}
          </Button>
        </CardBody>
      </Card>

      <Card className="flex min-h-0 flex-col lg:max-h-[calc(100vh-14rem)]">
        <CardHeader>
          <CardTitle>
            {matrixQuery.isLoading
              ? "Training matrix"
              : `Showing ${visible.length} of ${payload?.rows.length ?? 0} people`}
          </CardTitle>
        </CardHeader>
        <CardBody className="min-h-0 overflow-auto">
          {matrixQuery.isError ? (
            <p className="text-sm text-rose-700" role="alert">
              {(matrixQuery.error as Error).message}
            </p>
          ) : (payload?.skills.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-700">
              No active skills yet.{" "}
              <Link className="text-brand-700 hover:underline" to="/people/skills">
                Add a skill
              </Link>
              .
            </p>
          ) : (
            <table className="min-w-full border-collapse text-sm">
              <thead className="sticky top-0 z-20 border-b border-slate-200 bg-white">
                <tr>
                  <th className="sticky left-0 z-30 bg-white px-2 py-2 text-left text-xs font-medium uppercase text-slate-600">
                    Person
                  </th>
                  {payload?.skills.map((skill) => (
                    <th key={skill.id} className="px-2 py-2 text-left text-xs font-medium uppercase text-slate-600">
                      {skill.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.staffId} className="border-b border-slate-100">
                    <td className="sticky left-0 z-10 bg-white px-2 py-2">
                      <Link className="text-brand-700 hover:underline" to={`/people/staff/${row.staffId}`}>
                        {row.fullName}
                      </Link>
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.skillId}
                        className={cell.status === "expired" ? "px-2 py-2 text-rose-700" : "px-2 py-2 text-slate-700"}
                      >
                        {cellText(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
